import React, { useEffect, useMemo, useState } from 'react';
import { signOut } from 'firebase/auth';
import { auth } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { getMarksForMyChildren } from '../api/marksApi';
import { getFeesForMyChildren } from '../api/feesApi';
import { listAttendance } from '../api/attendanceApi';
import { listSyllabusForClass, listAssessmentMarksForStudent } from '../api/academicsApi';
import { getStudentClassForProfile } from '../api/studentsApi';
import { AppHeader } from './LoginPanel';
import { formatClassLabel } from '../constants/classes';
import { matchesSearch } from '../utils/searchFilter';
import { buildSchoolUpiLink } from '../utils/upiPayment';

export default function ParentPanel() {
  const { user, profile } = useAuth();
  const [tab, setTab] = useState('marks');
  const [marks, setMarks] = useState([]);
  const [examMarks, setExamMarks] = useState([]);
  const [fees, setFees] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [syllabus, setSyllabus] = useState([]);
  const [studentClass, setStudentClass] = useState('');
  const [classLoading, setClassLoading] = useState(true);
  const [err, setErr] = useState('');
  const [tableFilter, setTableFilter] = useState('');

  useEffect(() => {
    setTableFilter('');
  }, [tab]);

  useEffect(() => {
    async function loadSafe(label, fn, fallback) {
      try {
        return await fn();
      } catch (e) {
        const msg = e?.message || String(e);
        if (!String(msg).toLowerCase().includes('permission')) {
          setErr((prev) => prev || `${label}: ${msg}`);
        }
        return fallback;
      }
    }

    async function load() {
      setErr('');
      if (profile?.role === 'STUDENT') setClassLoading(true);
      const cls =
        profile?.role === 'STUDENT'
          ? await loadSafe('Class', () => getStudentClassForProfile(profile), '')
          : '';
      setStudentClass(cls);
      if (profile?.role === 'STUDENT') setClassLoading(false);

      const m = await loadSafe('Marks', () => getMarksForMyChildren(profile), []);
      const f = await loadSafe('Fees', () => getFeesForMyChildren(profile), []);
      const a = await loadSafe('Attendance', () => listAttendance(), []);
      const em =
        profile?.role === 'STUDENT'
          ? await loadSafe('Exam marks', () => listAssessmentMarksForStudent(profile.admno, profile.studentCsvId), [])
          : [];
      const syl = cls
        ? await loadSafe('Syllabus', () => listSyllabusForClass(cls), [])
        : [];

      setMarks(m);
      setFees(f);
      setAttendance(profile?.role === 'STUDENT' ? a : a.slice(0, 20));
      setExamMarks(em);
      setSyllabus(syl);
    }
    load();
  }, [profile]);

  const filteredMarks = useMemo(() => {
    const legacy = marks.map((m) => ({ ...m, kind: 'Mark' }));
    const exams = examMarks.map((m) => ({
      id: m.id,
      studentName: m.studentName,
      class: m.class,
      subject: m.subject,
      score: `${m.score}/${m.maxMarks || 100}`,
      kind: 'Exam/Test',
    }));
    return [...legacy, ...exams].filter((m) =>
      matchesSearch(tableFilter, [m.studentName, m.id, m.class, m.subject, String(m.score ?? ''), m.kind])
    );
  }, [marks, examMarks, tableFilter]);

  const filteredFees = useMemo(
    () =>
      fees.filter((f) =>
        matchesSearch(tableFilter, [f.studentName, f.id, String(f.amountDue ?? ''), (f.monthsPaid || []).join(', ')])
      ),
    [fees, tableFilter]
  );
  const studentAttendanceIds = useMemo(() => {
    if (profile?.role !== 'STUDENT') return null;
    return new Set([profile.admno, profile.studentCsvId].filter(Boolean).map(String));
  }, [profile]);

  const attendanceWithStatus = useMemo(() => {
    return attendance.map((row) => {
      if (!studentAttendanceIds?.size) return { ...row, myStatus: null };
      const ids = studentAttendanceIds;
      const inPresent = (row.presentStudents || []).some((id) => ids.has(String(id).trim()));
      const inAbsent = (row.absentStudents || []).some((id) => ids.has(String(id).trim()));
      let myStatus = null;
      if (inPresent) myStatus = 'Present';
      else if (inAbsent) myStatus = 'Absent';
      return { ...row, myStatus };
    });
  }, [attendance, studentAttendanceIds]);

  const filteredAttendance = useMemo(() => {
    let rows = attendanceWithStatus;
    if (studentAttendanceIds?.size) {
      rows = rows.filter((row) => row.myStatus != null);
    }
    return rows.filter((row) =>
      matchesSearch(tableFilter, [
        row.date,
        row.class,
        row.myStatus || '',
        (row.presentStudents || []).join(', '),
        (row.absentStudents || []).join(', '),
      ])
    );
  }, [attendanceWithStatus, tableFilter, studentAttendanceIds]);

  const filteredSyllabus = useMemo(
    () => syllabus.filter((s) => matchesSearch(tableFilter, [s.subject, s.content])),
    [syllabus, tableFilter]
  );

  return (
    <div className="mps-app">
      <AppHeader
        title={profile?.role === 'STUDENT' ? 'Student dashboard' : 'Parent dashboard'}
        user={user}
        profile={profile}
        onSignOut={() => auth && signOut(auth)}
      />
      <main className="mps-main">
        {profile?.role === 'STUDENT' ? (
          <>
            <div className="mps-card mps-student-welcome-banner">
              <span className="mps-student-welcome-sign" aria-hidden="true">🙏</span>
              <div>
                <h2 className="mps-student-welcome-title">Welcome, {profile?.name || 'Student'}!</h2>
                <p className="mps-muted mps-student-welcome-lead">Modern Public School student portal — view your marks, syllabus, fees and attendance.</p>
              </div>
            </div>
            <div className="mps-card mps-student-class-banner">
              <strong>Your class:</strong>{' '}
              {classLoading ? 'Loading…' : studentClass ? formatClassLabel(studentClass) : '—'}
            </div>
          </>
        ) : null}

        <div className="mps-tabs">
          <button type="button" className={`mps-tab ${tab === 'marks' ? 'mps-tab-active' : ''}`} onClick={() => setTab('marks')}>
            Marks
          </button>
          {profile?.role === 'STUDENT' ? (
            <button type="button" className={`mps-tab ${tab === 'syllabus' ? 'mps-tab-active' : ''}`} onClick={() => setTab('syllabus')}>
              Syllabus
            </button>
          ) : null}
          <button type="button" className={`mps-tab ${tab === 'fees' ? 'mps-tab-active' : ''}`} onClick={() => setTab('fees')}>
            {profile?.role === 'STUDENT' ? 'Dues' : 'Fees'}
          </button>
          <button type="button" className={`mps-tab ${tab === 'attendance' ? 'mps-tab-active' : ''}`} onClick={() => setTab('attendance')}>
            Attendance
          </button>
        </div>

        {err ? <div className="mps-msg mps-msg-error">{err}</div> : null}

        {tab === 'marks' ? (
          <div className="mps-card">
            <h2>{profile?.role === 'STUDENT' ? 'Your marks' : "Your children's marks"}</h2>
            {filteredMarks.length === 0 ? (
              <p className="mps-msg mps-msg-info">No marks records linked to your account yet.</p>
            ) : (
              <>
                <input
                  type="search"
                  className="mps-table-filter"
                  value={tableFilter}
                  onChange={(e) => setTableFilter(e.target.value)}
                  placeholder="Search student, class, subject, score…"
                  aria-label="Filter marks"
                />
                <table className="mps-table">
                  <thead>
                    <tr>
                      <th>Type</th>
                      <th>Student</th>
                      <th>Class</th>
                      <th>Subject</th>
                      <th>Score</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredMarks.map((m) => (
                      <tr key={`${m.kind}-${m.id}`}>
                        <td>{m.kind}</td>
                        <td>
                          {m.studentName || m.id} ({m.id})
                        </td>
                        <td>{formatClassLabel(m.class)}</td>
                        <td>{m.subject}</td>
                        <td>{m.score}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
          </div>
        ) : null}

        {tab === 'syllabus' && profile?.role === 'STUDENT' ? (
          <div className="mps-card">
            <h2>Syllabus — class {formatClassLabel(studentClass)}</h2>
            {filteredSyllabus.length === 0 ? (
              <p className="mps-msg mps-msg-info">No syllabus published for your class yet.</p>
            ) : (
              <>
                <input
                  type="search"
                  className="mps-table-filter"
                  value={tableFilter}
                  onChange={(e) => setTableFilter(e.target.value)}
                  placeholder="Search subject…"
                  aria-label="Filter syllabus"
                />
                <ul className="mps-syllabus-list">
                  {filteredSyllabus.map((row) => (
                    <li key={row.id}>
                      <h3>{row.subject}</h3>
                      <pre className="mps-syllabus-preview">{row.content}</pre>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        ) : null}

        {tab === 'fees' ? (
          <div className="mps-card">
            <h2>{profile?.role === 'STUDENT' ? 'Fee dues' : 'Fee status'}</h2>
            {profile?.role === 'STUDENT' && fees.some((f) => Number(f.amountDue) > 0) ? (
              <p className="mps-muted" style={{ marginTop: 0, marginBottom: 12 }}>
                Pay school dues via UPI (Google Pay, PhonePe, Paytm). Amount is pre-filled from your dues record.
              </p>
            ) : null}
            {fees.length === 0 ? (
              <p className="mps-msg mps-msg-info">
                {profile?.role === 'STUDENT'
                  ? 'No dues record for your admission number yet. Contact the school office.'
                  : 'No fee records linked to your account yet.'}
              </p>
            ) : (
              <>
                <input
                  type="search"
                  className="mps-table-filter"
                  value={tableFilter}
                  onChange={(e) => setTableFilter(e.target.value)}
                  placeholder="Search student, amount, months paid…"
                  aria-label="Filter fees"
                />
                <table className="mps-table">
                  <thead>
                    <tr>
                      <th>Student</th>
                      <th>Amount due</th>
                      <th>Months paid</th>
                      {profile?.role === 'STUDENT' ? <th>Pay</th> : null}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredFees.map((f) => {
                      const upiLink =
                        profile?.role === 'STUDENT'
                          ? buildSchoolUpiLink({
                              amountDue: f.amountDue,
                              studentName: f.studentName || profile?.name || f.id,
                            })
                          : null;
                      return (
                        <tr key={f.id}>
                          <td>
                            {f.studentName || f.id} ({f.id})
                          </td>
                          <td>₹{f.amountDue}</td>
                          <td>{(f.monthsPaid || []).join(', ') || 'None'}</td>
                          {profile?.role === 'STUDENT' ? (
                            <td>
                              {upiLink ? (
                                <a href={upiLink} className="mps-btn mps-btn-sm mps-btn-secondary mps-upi-pay-btn">
                                  Pay ₹{f.amountDue} via UPI
                                </a>
                              ) : (
                                <span className="mps-muted">—</span>
                              )}
                            </td>
                          ) : null}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </>
            )}
          </div>
        ) : null}

        {tab === 'attendance' ? (
          <div className="mps-card">
            <h2>{profile?.role === 'STUDENT' ? 'My attendance' : 'School attendance (recent)'}</h2>
            <p style={{ fontSize: '0.85rem', color: '#566573', marginBottom: 12 }}>
              {profile?.role === 'STUDENT'
                ? 'Your present / absent status for recent class attendance.'
                : "Compare your child's ID with present lists below."}
            </p>
            <input
              type="search"
              className="mps-table-filter"
              value={tableFilter}
              onChange={(e) => setTableFilter(e.target.value)}
              placeholder="Search date, class, present student IDs…"
              aria-label="Filter attendance"
            />
            <table className="mps-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Class</th>
                  {profile?.role === 'STUDENT' ? <th>Status</th> : <th>Present students</th>}
                </tr>
              </thead>
              <tbody>
                {filteredAttendance.length === 0 ? (
                  <tr>
                    <td colSpan={profile?.role === 'STUDENT' ? 3 : 3} className="mps-muted">
                      {profile?.role === 'STUDENT' ? 'No attendance recorded for you yet.' : 'No matching records.'}
                    </td>
                  </tr>
                ) : (
                  filteredAttendance.map((row) => (
                    <tr key={row.id}>
                      <td>{row.date}</td>
                      <td>{formatClassLabel(row.class)}</td>
                      {profile?.role === 'STUDENT' ? (
                        <td>
                          <span className={`mps-badge mps-badge-${row.myStatus === 'Present' ? 'parent' : 'admin'}`}>
                            {row.myStatus}
                          </span>
                        </td>
                      ) : (
                        <td>{(row.presentStudents || []).join(', ') || '—'}</td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        ) : null}
      </main>
    </div>
  );
}
